/**
 * In-designer "Run in preview" output panel.
 *
 * Pairs with `<ScriptEditor>`'s `onRunPreview` keybinding (Shift-Cmd-Enter)
 * to give admins a fast feedback loop without leaving the Layout Designer.
 * Spins up its own iframe sandbox via `createScriptSandbox` — same security
 * posture as production, just routed to an in-panel display instead of the
 * live LayoutRenderer's bus / actions / fetch handlers.
 *
 * What gets shown:
 *   - **Result** — the script's return value (relevant for transforms; ignored at runtime for widget bodies but still useful at preview time).
 *   - **Logs** — every `ctx.log.{info,warn,error}` call the script makes.
 *   - **Bus publishes** — each `ctx.bus.publish(topic, payload)` is captured rather than dispatched.
 *   - **Action invocations** — each `ctx.invokeAction(widgetId, action, payload)` is captured.
 *   - **Fetches** — each `ctx.fetch(url, opts)` is captured and answered with a stub `{ ok: true, status: 200, json: 'preview' }` so chained logic still runs.
 *   - **Errors / timeouts** — surfaced inline.
 *
 * The wrapper component `ScriptEditorWithPreview` plumbs the runner ref
 * automatically so callers don't need to deal with imperative handles.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import ScriptEditor from './scriptEditor';
import { createScriptSandbox } from './scriptSandbox';
import { sdkHelpers } from './sdkHelpers';
import { buildCtxForInvoke } from './sdkRuntime';

const PREVIEW_TIMEOUT_MS = 5000;

function samplePayload() {
  return null;
}

function sampleCase() {
  return {
    id: 1,
    case_type_id: 1,
    case_type_name: 'SampleType',
    case_number: 'C-PREVIEW',
    status: 'open',
    custom_fields: {},
  };
}

function sampleWorkItem() {
  return {
    id: 1,
    case_id: 1,
    step_name: 'preview-step',
    status: 'in_progress',
    assignee_user_id: null,
    response_data_json: null,
  };
}

function sampleDraft(category) {
  return {
    case_type_id: category === 'add_case' ? 1 : undefined,
    task_type: category === 'add_task' ? 'preview' : undefined,
    fields: {},
  };
}

function buildPreviewLayoutCtx(category, workspaceId) {
  return {
    workspaceId: workspaceId || null,
    category: category || null,
    role: { id: null, name: 'preview' },
    currentUser: { id: 0, email: 'preview@local', display_name: 'Preview' },
    case: category === 'home' || category === 'add_case' ? null : sampleCase(),
    workItem: category === 'task' ? sampleWorkItem() : null,
    draft: category === 'add_case' || category === 'add_task' ? sampleDraft(category) : null,
  };
}

const panelStyles = {
  shell: {
    border: '1px solid var(--border-color)',
    borderRadius: 6,
    background: 'var(--bg-tertiary, #2a2a3a)',
    fontSize: 11,
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px', borderBottom: '1px solid var(--border-color)',
  },
  runBtn: {
    background: 'var(--accent-color, #4a7eff)', color: '#fff',
    border: 'none', borderRadius: 4, padding: '3px 10px',
    fontSize: 11, cursor: 'pointer', fontWeight: 600,
  },
  runBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  hint: { color: 'var(--text-secondary)', fontSize: 10, fontStyle: 'italic' },
  body: { padding: '8px 10px', maxHeight: 220, overflow: 'auto' },
  section: { marginBottom: 8 },
  sectionLabel: {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em',
    color: 'var(--text-secondary)', marginBottom: 2,
  },
  pre: {
    margin: 0, fontSize: 11, fontFamily: "'Fira Code', monospace",
    whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    color: 'var(--text-primary)',
  },
  logRow: { display: 'flex', gap: 6, padding: '1px 0' },
  logLevelInfo: { color: '#6cb' },
  logLevelWarn: { color: '#e8b900' },
  logLevelError: { color: '#e85a5a' },
  errorBox: {
    padding: 6, borderRadius: 4, background: 'rgba(232, 90, 90, 0.15)',
    border: '1px solid rgba(232, 90, 90, 0.4)', color: '#e85a5a',
  },
  empty: { color: 'var(--text-secondary)', fontStyle: 'italic' },
};

function fmt(v) {
  if (v === undefined) return 'undefined';
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

function ScriptPreviewPanel({
  value,
  kind = 'script',
  category,
  workspaceId,
  onRegisterRunner,
  onToggleExpand,
  parentExpanded,
}) {
  const sandboxRef = useRef(null);
  const [output, setOutput] = useState({
    logs: [], busPublishes: [], invokeActions: [], fetches: [],
    result: null, hasResult: false, error: null, running: false, ranAt: null,
  });

  const ensureSandbox = useCallback(() => {
    if (sandboxRef.current) return sandboxRef.current;
    const sb = createScriptSandbox({
      onBusPublish: (topic, payload) => {
        setOutput((o) => ({ ...o, busPublishes: [...o.busPublishes, { topic, payload }] }));
      },
      onInvokeAction: (widgetId, action, payload) => {
        setOutput((o) => ({ ...o, invokeActions: [...o.invokeActions, { widgetId, action, payload }] }));
      },
      onFetchRequest: async (msg) => {
        setOutput((o) => ({
          ...o,
          fetches: [...o.fetches, { url: msg.url, method: msg.method || 'GET' }],
        }));
        return { ok: true, status: 200, headers: {}, text: '"preview"', json: 'preview' };
      },
      onLog: (level, args) => {
        setOutput((o) => ({ ...o, logs: [...o.logs, { level, args }] }));
      },
    });
    sb.setHelpers(sdkHelpers);
    sandboxRef.current = sb;
    return sb;
  }, []);

  const run = useCallback(async (source) => {
    const body = (source ?? '').toString();
    if (!body.trim()) {
      setOutput({
        logs: [], busPublishes: [], invokeActions: [], fetches: [],
        result: null, hasResult: false, error: 'Editor is empty.', running: false, ranAt: Date.now(),
      });
      return;
    }
    setOutput({
      logs: [], busPublishes: [], invokeActions: [], fetches: [],
      result: null, hasResult: false, error: null, running: true, ranAt: Date.now(),
    });
    try {
      const sb = ensureSandbox();
      const layoutCtx = buildPreviewLayoutCtx(category, workspaceId);
      const ctxData = buildCtxForInvoke(layoutCtx, { id: 'preview', type: kind === 'transform' ? 'wire.transform' : 'widget.script' });
      const result = await sb.invoke(kind, body, samplePayload(), ctxData, {
        source_widget_id: 'preview',
        timeout_ms: PREVIEW_TIMEOUT_MS,
      });
      setOutput((o) => ({ ...o, result, hasResult: true, running: false }));
    } catch (e) {
      setOutput((o) => ({ ...o, error: (e && e.message) ? e.message : String(e), running: false }));
    }
  }, [category, kind, workspaceId, ensureSandbox]);

  // Hand the run function to the editor wrapper so Shift-Cmd-Enter triggers it.
  useEffect(() => {
    if (typeof onRegisterRunner === 'function') onRegisterRunner(run);
    return () => { if (typeof onRegisterRunner === 'function') onRegisterRunner(null); };
  }, [run, onRegisterRunner]);

  // Tear down the sandbox on unmount — wedged previews can't outlive the editor.
  useEffect(() => () => {
    try { sandboxRef.current?.destroy(); } catch (_) { /* swallow */ }
    sandboxRef.current = null;
  }, []);

  const empty = !output.running
    && !output.error
    && !output.hasResult
    && output.logs.length === 0
    && output.busPublishes.length === 0
    && output.invokeActions.length === 0
    && output.fetches.length === 0;

  return (
    <div style={panelStyles.shell}>
      <div style={panelStyles.header}>
        <button
          type="button"
          onClick={() => run(value)}
          disabled={output.running}
          style={{ ...panelStyles.runBtn, ...(output.running ? panelStyles.runBtnDisabled : null) }}
          title="Run preview (Shift+⌘+Enter inside the editor)"
        >
          {output.running ? '… Running' : '▶ Run preview'}
        </button>
        <span style={panelStyles.hint}>
          Sandbox; bus / actions / fetch are captured, not dispatched.
        </span>
        {onToggleExpand && (
          <button
            type="button"
            onClick={onToggleExpand}
            title={parentExpanded ? 'Collapse editor + preview (Esc)' : 'Expand editor + preview'}
            aria-label={parentExpanded ? 'Collapse' : 'Expand editor and preview'}
            style={{
              marginLeft: 'auto',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 12, lineHeight: 1, cursor: 'pointer',
            }}
          >
            {parentExpanded ? '⤡' : '⤢'}
          </button>
        )}
      </div>
      <div style={panelStyles.body}>
        {empty && (
          <div style={panelStyles.empty}>No output yet — run the script to see results, logs, and intercepted side-effects.</div>
        )}
        {output.error && (
          <div style={{ ...panelStyles.section, ...panelStyles.errorBox }}>
            <div style={panelStyles.sectionLabel}>Error</div>
            <pre style={panelStyles.pre}>{output.error}</pre>
          </div>
        )}
        {output.hasResult && (
          <div style={panelStyles.section}>
            <div style={panelStyles.sectionLabel}>Result</div>
            <pre style={panelStyles.pre}>{fmt(output.result)}</pre>
          </div>
        )}
        {output.logs.length > 0 && (
          <div style={panelStyles.section}>
            <div style={panelStyles.sectionLabel}>Logs ({output.logs.length})</div>
            {output.logs.map((l, i) => (
              <div key={i} style={panelStyles.logRow}>
                <span style={
                  l.level === 'error' ? panelStyles.logLevelError
                    : l.level === 'warn' ? panelStyles.logLevelWarn
                      : panelStyles.logLevelInfo
                }>{l.level}</span>
                <span style={{ ...panelStyles.pre, flex: 1 }}>
                  {(Array.isArray(l.args) ? l.args : []).map(fmt).join(' ')}
                </span>
              </div>
            ))}
          </div>
        )}
        {output.busPublishes.length > 0 && (
          <div style={panelStyles.section}>
            <div style={panelStyles.sectionLabel}>Bus publishes ({output.busPublishes.length})</div>
            {output.busPublishes.map((p, i) => (
              <pre key={i} style={panelStyles.pre}>{p.topic} → {fmt(p.payload)}</pre>
            ))}
          </div>
        )}
        {output.invokeActions.length > 0 && (
          <div style={panelStyles.section}>
            <div style={panelStyles.sectionLabel}>Action invocations ({output.invokeActions.length})</div>
            {output.invokeActions.map((a, i) => (
              <pre key={i} style={panelStyles.pre}>{a.widgetId}.{a.action}({fmt(a.payload)})</pre>
            ))}
          </div>
        )}
        {output.fetches.length > 0 && (
          <div style={panelStyles.section}>
            <div style={panelStyles.sectionLabel}>Fetches ({output.fetches.length})</div>
            {output.fetches.map((f, i) => (
              <pre key={i} style={panelStyles.pre}>{f.method} {f.url}</pre>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Drop-in replacement for `<ScriptEditor>` that adds a "Run preview" panel
 * underneath. Plumbs the runner ref so the editor's Shift-Cmd-Enter binding
 * fires the panel's `run()` automatically.
 *
 * Extra props vs ScriptEditor:
 *   - `kind`           'script' (default) | 'transform'
 *   - `category`       layout category for ctx narrowing + sample data
 *   - `workspaceId`    threaded into preview ctx
 *   - `caseTypeSchema` forwarded to ScriptEditor for custom-fields completion
 */
export default function ScriptEditorWithPreview({
  value,
  onChange,
  placeholder,
  minHeight = 160,
  language = 'javascript',
  kind = 'script',
  category,
  workspaceId,
  caseTypeSchema,
}) {
  const runnerRef = useRef(null);
  const handleRegister = useCallback((fn) => {
    runnerRef.current = fn;
  }, []);
  const handleRunPreview = useCallback((currentValue) => {
    if (typeof runnerRef.current === 'function') runnerRef.current(currentValue);
  }, []);

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const wrapStyle = expanded
    ? {
      position: 'fixed',
      top: '4vh', left: '4vw', right: '4vw', bottom: '4vh',
      zIndex: 100,
      background: 'var(--bg-secondary, #1e1e2e)',
      border: '1px solid var(--border-color)',
      borderRadius: 8,
      padding: 12,
      display: 'flex', flexDirection: 'column', gap: 8,
      boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
    }
    : { display: 'flex', flexDirection: 'column', gap: 6 };

  const editorSlotStyle = expanded
    ? { flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }
    : null;
  const previewSlotStyle = expanded
    ? { flex: 2, minHeight: 0, overflow: 'auto' }
    : null;

  return (
    <>
      {expanded && (
        <div
          onMouseDown={() => setExpanded(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0, 0, 0, 0.55)' }}
        />
      )}
      <div style={wrapStyle}>
        <div style={editorSlotStyle || undefined}>
          <ScriptEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            minHeight={minHeight}
            language={language}
            categoryHint={category}
            caseTypeSchema={caseTypeSchema}
            onRunPreview={handleRunPreview}
            expandable={false}
            forceFillHeight={expanded}
          />
        </div>
        <div style={previewSlotStyle || undefined}>
          <ScriptPreviewPanel
            value={value}
            kind={kind}
            category={category}
            workspaceId={workspaceId}
            onRegisterRunner={handleRegister}
            onToggleExpand={() => setExpanded((e) => !e)}
            parentExpanded={expanded}
          />
        </div>
      </div>
    </>
  );
}
